import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Circle, PersonAdd } from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addMinutes, format } from 'date-fns';
import { toast } from 'react-toastify';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { treatmentsAPI } from '../../api/treatments';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

// Appointment types and durations are static data, not from API

// Appointment types
const APPOINTMENT_TYPES = [
  { value: 'New Consultation', label: 'New Consultation' },
  { value: 'Follow-up', label: 'Follow-up' },
  { value: 'Emergency', label: 'Emergency' },
  { value: 'Cleaning', label: 'Cleaning' },
  { value: 'Filling', label: 'Filling' },
  { value: 'Root Canal', label: 'Root Canal' },
  { value: 'Extraction', label: 'Extraction' },
  { value: 'Crown/Bridge Work', label: 'Crown/Bridge Work' },
  { value: 'Implant', label: 'Implant' },
  { value: 'Orthodontic Adjustment', label: 'Orthodontic Adjustment' },
  { value: 'Denture Fitting', label: 'Denture Fitting' },
  { value: 'Surgical Procedure', label: 'Surgical Procedure' },
  { value: 'Other', label: 'Other' },
];

// Appointment durations (in minutes)
const APPOINTMENT_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

const AddAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        setError(null);

        // Get token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Make API calls to get necessary data
        const patientsResponse = await axios.get(`${API_URL}/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const doctorsResponse = await axios.get(`${API_URL}/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const clinicsResponse = await axios.get(`${API_URL}/clinics`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const defsResponse = await treatmentsAPI.getDefinitions();
        
        // Set data from API responses
        setPatients(patientsResponse.data.data);
        setDoctors(doctorsResponse.data.data.filter(staff => staff.role === 'dentist'));
        setClinics(clinicsResponse.data.data);
        setDefinitions(defsResponse.data || defsResponse);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(formatFailedActionMessage('load', 'appointment form'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return null;
    return addMinutes(new Date(startTime), durationMinutes);
  };

  // Get date/time from URL parameters if available
  const initialDateTime = useMemo(() => {
    const startDateParam = searchParams.get('startDate');
    const durationParam = searchParams.get('duration');
    
    if (startDateParam) {
      try {
        const startDate = new Date(decodeURIComponent(startDateParam));
        if (!isNaN(startDate.getTime())) {
          // Extract date (without time)
          const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          
          // Extract time (create a date object with just the time part for TimePicker)
          // TimePicker expects a Date object, so we create one with today's date but the selected time
          const startTime = new Date();
          startTime.setHours(startDate.getHours());
          startTime.setMinutes(startDate.getMinutes());
          startTime.setSeconds(0);
          startTime.setMilliseconds(0);
          
          // Get duration from URL or default to 30
          const duration = durationParam ? parseInt(durationParam, 10) : 30;
          
          return {
            date: date,
            startTime: startTime,
            duration: duration,
          };
        }
      } catch (err) {
        console.error('Error parsing startDate from URL:', err);
      }
    }
    return {
      date: null,
      startTime: null,
      duration: 30,
    };
  }, [searchParams]);

  const formik = useFormik({
    initialValues: {
      patientId: searchParams.get('patientId') || '',
      doctorId: searchParams.get('doctorId') || '',
      clinicId: searchParams.get('clinicId') || '',
      date: initialDateTime.date,
      startTime: initialDateTime.startTime,
      duration: initialDateTime.duration,
      endTime: null,
      type: '',
      status: 'scheduled',
      color: '', // Optional custom color for calendar event
      reasonForVisit: '',
      notes: '',
      treatmentId: '',
    },
    validationSchema: Yup.object({
      patientId: Yup.string().required('Patient is required'),
      doctorId: Yup.string().required('Doctor is required'),
      clinicId: Yup.string().required('Clinic is required'),
      date: Yup.date().required('Date is required').nullable(),
      startTime: Yup.date().required('Start time is required').nullable(),
      duration: Yup.number().required('Duration is required'),
      type: Yup.string().required('Appointment type is required'),
      reasonForVisit: Yup.string().required('Reason for visit is required'),
      notes: Yup.string(),
    }),
    onSubmit: async (values) => {
      // Additional validation before submission
      const errors = {};
      if (!values.patientId) errors.patientId = 'Patient is required';
      if (!values.doctorId) errors.doctorId = 'Doctor is required';
      if (!values.clinicId) errors.clinicId = 'Clinic is required';
      if (!values.type) errors.type = 'Appointment type is required';
      if (!values.date) errors.date = 'Appointment date is required';
      if (!values.startTime) errors.startTime = 'Start time is required';
      if (!values.reasonForVisit) errors.reasonForVisit = 'Reason for visit is required';
      
      if (Object.keys(errors).length > 0) {
        // Set all errors at once
        Object.keys(errors).forEach(key => {
          formik.setFieldError(key, errors[key]);
          formik.setFieldTouched(key, true);
        });
        toast.error('Required fields are missing');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
       
        // Get token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }
        
        // Combine date and time into a single datetime
        // DatePicker gives us the date, TimePicker gives us the time
        // We need to combine them properly
        const appointmentDate = new Date(values.date);
        const appointmentTime = new Date(values.startTime);
        
        const appointmentDateTime = new Date(
          appointmentDate.getFullYear(),
          appointmentDate.getMonth(),
          appointmentDate.getDate(),
          appointmentTime.getHours(),
          appointmentTime.getMinutes(),
          0
        );
        
        // Calculate end time based on the combined datetime
        const endTime = calculateEndTime(appointmentDateTime, values.duration);
        
        // Prepare appointment data
        const appointmentData = {
          patient: values.patientId,
          clinic: values.clinicId,
          dentist: values.doctorId,
          appointmentDate: appointmentDateTime.toISOString(),
          endTime: endTime ? endTime.toISOString() : null,
          duration: values.duration,
          appointmentType: values.type,
          reasonForVisit: values.reasonForVisit,
          notes: values.notes || '',
          status: 'scheduled',
          ...(values.treatmentId ? { treatment: values.treatmentId } : {}),
          ...(values.color ? { color: values.color } : {}),
        };
        
        // Make API call to create appointment
        const response = await axios.post(`${API_URL}/appointments`, appointmentData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        toast.success(formatSuccessActionMessage('scheduled', 'Appointment'));
        navigate('/calendar');
      } catch (err) {
        console.error('Error creating appointment:', err);
        setError(getApiErrorMessage(err, formatFailedActionMessage('schedule', 'appointment')));
      } finally {
        setLoading(false);
      }
    },
  });

  // Update end time whenever start time or duration changes
  useEffect(() => {
    if (formik.values.startTime && formik.values.duration) {
      const endTime = calculateEndTime(formik.values.startTime, formik.values.duration);
      formik.setFieldValue('endTime', endTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.startTime, formik.values.duration]);

  // Check if returning from patient creation and restore form data
  useEffect(() => {
    if (initialLoading) return;
    
    const savedFormData = sessionStorage.getItem('appointmentFormData');
    const newPatientId = searchParams.get('newPatientId');
    if (savedFormData && newPatientId) {
      try {
        const formData = JSON.parse(savedFormData);
        // Restore form data
        if (formData.date) formik.setFieldValue('date', new Date(formData.date));
        if (formData.startTime) formik.setFieldValue('startTime', new Date(formData.startTime));
        if (formData.duration) formik.setFieldValue('duration', formData.duration);
        if (formData.doctorId) formik.setFieldValue('doctorId', formData.doctorId);
        if (formData.clinicId) formik.setFieldValue('clinicId', formData.clinicId);
        if (formData.type) formik.setFieldValue('type', formData.type);
        if (formData.reasonForVisit) formik.setFieldValue('reasonForVisit', formData.reasonForVisit);
        if (formData.notes) formik.setFieldValue('notes', formData.notes);
        if (formData.treatmentId) formik.setFieldValue('treatmentId', formData.treatmentId);
        
        // Set the newly created patient
        formik.setFieldValue('patientId', newPatientId);
        
        // Clear saved data
        sessionStorage.removeItem('appointmentFormData');
        
        // Remove newPatientId from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('newPatientId');
        navigate(`/appointments/add?${newSearchParams.toString()}`, { replace: true });
        
        toast.success(`${formatSuccessActionMessage('created', 'Patient')}. Complete the appointment details.`);
      } catch (err) {
        console.error('Error restoring form data:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoading, searchParams]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Schedule New Appointment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fields marked with * are required
        </Typography>
        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Patient Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Patient Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    id="patientId"
                    options={patients}
                    getOptionLabel={(option) => `${option.name} (${option.phone})`}
                    value={patients.find(p => p._id === formik.values.patientId) || null}
                    onChange={(event, newValue) => {
                      formik.setFieldValue('patientId', newValue ? newValue._id : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Patient *"
                        error={formik.touched.patientId && Boolean(formik.errors.patientId)}
                        helperText={formik.touched.patientId && formik.errors.patientId}
                        disabled={loading}
                        required
                      />
                    )}
                    disabled={loading}
                  />
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PersonAdd />}
                  onClick={() => {
                    // Save current form data to sessionStorage
                    const formData = {
                      date: formik.values.date ? formik.values.date.toISOString() : null,
                      startTime: formik.values.startTime ? formik.values.startTime.toISOString() : null,
                      duration: formik.values.duration,
                      doctorId: formik.values.doctorId,
                      clinicId: formik.values.clinicId,
                      type: formik.values.type,
                      reasonForVisit: formik.values.reasonForVisit,
                      notes: formik.values.notes,
                      treatmentId: formik.values.treatmentId,
                    };
                    sessionStorage.setItem('appointmentFormData', JSON.stringify(formData));
                    navigate('/patients/add?returnTo=appointment');
                  }}
                  disabled={loading}
                  sx={{ mt: 0.5 }}
                >
                  New Patient
                </Button>
              </Box>
            </Grid>

            {/* Appointment Details */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Appointment Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={formik.touched.clinicId && Boolean(formik.errors.clinicId)}>
                <InputLabel id="clinic-label">Clinic *</InputLabel>
                <Select
                  labelId="clinic-label"
                  id="clinicId"
                  name="clinicId"
                  value={formik.values.clinicId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Clinic"
                  disabled={loading}
                >
                  {clinics.map((clinic) => (
                    <MenuItem key={clinic._id} value={clinic._id}>
                      {clinic.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.clinicId && formik.errors.clinicId && (
                  <FormHelperText>{formik.errors.clinicId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={formik.touched.doctorId && Boolean(formik.errors.doctorId)}>
                <InputLabel id="doctor-label">Doctor *</InputLabel>
                <Select
                  labelId="doctor-label"
                  id="doctorId"
                  name="doctorId"
                  value={formik.values.doctorId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Doctor"
                  disabled={loading}
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor._id} value={doctor._id}>
                      {doctor.firstName} {doctor.lastName} ({doctor.specialization || 'General'})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.doctorId && formik.errors.doctorId && (
                  <FormHelperText>{formik.errors.doctorId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={formik.touched.type && Boolean(formik.errors.type)}>
                <InputLabel id="type-label">Appointment Type *</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Appointment Type"
                  disabled={loading}
                >
                  {APPOINTMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <FormHelperText>{formik.errors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Optional Treatment Link */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="treatment-label">Treatment (Optional)</InputLabel>
                <Select
                  labelId="treatment-label"
                  id="treatmentId"
                  name="treatmentId"
                  value={formik.values.treatmentId}
                  onChange={formik.handleChange}
                  label="Treatment (Optional)"
                  disabled={loading}
                  MenuProps={{ autoFocus: false, disableAutoFocusItem: true }}
                >
                  <MenuItem value="">
                    <em>No treatment</em>
                  </MenuItem>
                  {(() => {
                    const byCategory = (definitions || []).reduce((acc, t) => {
                      const cat = t.category || 'Other';
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(t);
                      return acc;
                    }, {});
                    const categories = Object.keys(byCategory).sort((a, b) => a.localeCompare(b));
                    return categories.map((category) => [
                      <ListSubheader key={`cat-${category}`} sx={{ bgcolor: 'grey.100', fontWeight: 600, lineHeight: 2 }}>
                        {category}
                      </ListSubheader>,
                      ...(byCategory[category] || [])
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((t) => (
                          <MenuItem key={t._id} value={t._id} sx={{ pl: 3 }}>
                            {t.name} ({t.code})
                          </MenuItem>
                        ))
                    ]);
                  })()}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Appointment Date *"
                  value={formik.values.date}
                  onChange={(date) => {
                    formik.setFieldValue('date', date);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      id="date"
                      name="date"
                      error={formik.touched.date && Boolean(formik.errors.date)}
                      helperText={formik.touched.date && formik.errors.date}
                      disabled={loading}
                    />
                  )}
                  disabled={loading}
                  disablePast
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              {isMobile ? (
                <TextField
                  fullWidth
                  label="Start Time *"
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formik.values.startTime ? format(formik.values.startTime, 'HH:mm') : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      formik.setFieldValue('startTime', null);
                      return;
                    }
                    const [h, m] = v.split(':').map(Number);
                    const d = new Date();
                    d.setHours(h, m, 0, 0);
                    formik.setFieldValue('startTime', d);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.startTime && Boolean(formik.errors.startTime)}
                  helperText={formik.touched.startTime && formik.errors.startTime}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 900 }}
                />
              ) : (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Start Time *"
                    value={formik.values.startTime}
                    onChange={(time) => {
                      formik.setFieldValue('startTime', time);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        id="startTime"
                        name="startTime"
                        error={formik.touched.startTime && Boolean(formik.errors.startTime)}
                        helperText={formik.touched.startTime && formik.errors.startTime}
                        disabled={loading}
                      />
                    )}
                    disabled={loading}
                    minutesStep={15}
                  />
                </LocalizationProvider>
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={formik.touched.duration && Boolean(formik.errors.duration)}>
                <InputLabel id="duration-label">Duration *</InputLabel>
                <Select
                  labelId="duration-label"
                  id="duration"
                  name="duration"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Duration"
                  disabled={loading}
                >
                  {APPOINTMENT_DURATIONS.map((duration) => (
                    <MenuItem key={duration.value} value={duration.value}>
                      {duration.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.duration && formik.errors.duration && (
                  <FormHelperText>{formik.errors.duration}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="End Time (Calculated)"
                  value={formik.values.endTime}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      id="endTime"
                      name="endTime"
                      disabled
                    />
                  )}
                  disabled
                  readOnly
                />
              </LocalizationProvider>
            </Grid>

            {/* Color Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="color-label">Event Color (Optional)</InputLabel>
                <Select
                  labelId="color-label"
                  id="color"
                  name="color"
                  value={formik.values.color}
                  onChange={formik.handleChange}
                  label="Event Color (Optional)"
                  disabled={loading}
                  renderValue={(value) => {
                    if (!value) return 'Default (based on status)';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Circle sx={{ color: value, fontSize: 20 }} />
                        <span>{value}</span>
                      </Box>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Default (based on status)</em>
                  </MenuItem>
                  <MenuItem value="#3f51b5">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#3f51b5', fontSize: 20 }} />
                      <span>Indigo</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#2196f3">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#2196f3', fontSize: 20 }} />
                      <span>Blue</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#4caf50">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#4caf50', fontSize: 20 }} />
                      <span>Green</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#ff9800">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#ff9800', fontSize: 20 }} />
                      <span>Orange</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#9c27b0">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#9c27b0', fontSize: 20 }} />
                      <span>Purple</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#e91e63">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#e91e63', fontSize: 20 }} />
                      <span>Pink</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#00bcd4">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#00bcd4', fontSize: 20 }} />
                      <span>Cyan</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="#795548">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ color: '#795548', fontSize: 20 }} />
                      <span>Brown</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="reasonForVisit"
                name="reasonForVisit"
                label="Reason for Visit"
                multiline
                rows={2}
                value={formik.values.reasonForVisit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reasonForVisit && Boolean(formik.errors.reasonForVisit)}
                helperText={formik.touched.reasonForVisit && formik.errors.reasonForVisit}
                disabled={loading}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes"
                multiline
                rows={4}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                disabled={loading}
                placeholder="Add any special instructions or notes about this appointment"
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/appointments')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? 'Scheduling...' : 'Schedule Appointment'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddAppointment;